import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useUnclaimedMoneyMultiStateSearch,
  useUnclaimedMoneySearch,
} from '@ltvco/refresh-lib/hooks';
import { Box, Grid } from '@ltvco/refresh-lib/theme';
import {
  searchFormsRecyclingValidation,
  useUnclaimedMoneyStatesOptionsAbbr,
} from '@ltvco/refresh-lib/utils';
import { useSession } from '@ltvco/refresh-lib/v1';
import { useForm, yupResolver } from '@ltvco/refresh-lib/vendors';
import React, { useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { SearchButton } from '../components';
import { SearchProps } from '../utils';

import {
  UnclaimedMoneySearchContainer,
  UnclaimedMoneySearchDivider,
  UnclaimedMoneyFormContainer,
} from './components';
import { UnclaimedMoneyInput } from './UnclaimedMoneyInput';
import { UnclaimedMoneyStatesSelect } from './UnclaimedMoneyStatesSelect';
import {
  getRedirectData,
  getRedirectURL,
  OptionType,
  schemaUnclaimedMoneySearchForm,
  IUnclaimedSearcParams,
  parseFullName,
  TSearchData,
} from './utils';

const nameErrorMessage = 'Please provide full name to search';
const statesErrorMessage = 'Please select a state to search';
const noResultsErrorMessage = 'No results found, please try again';

export const UnclaimedMoneySearch: React.FC<SearchProps> = ({
  isMobile,
  placeholderText,
  placement = 'dashboard',
}) => {
  const allStates = useUnclaimedMoneyStatesOptionsAbbr();
  const searchUnclaimedMoney = useUnclaimedMoneySearch(false);
  const searchUnclaimedMoneyMultiState = useUnclaimedMoneyMultiStateSearch();

  const navigate = useNavigate();

  const states = allStates.filter(
    (state) => state.value !== 'All' && state.display !== 'All'
  );

  const {
    session: { account },
  } = useSession();

  const { trackEventGA4 } = useContext(AppConfig);

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    control,
  } = useForm<{ name: string; states: OptionType[] }>({
    resolver: yupResolver(schemaUnclaimedMoneySearchForm),
    defaultValues: {
      name: '',
      states: [],
    },
  });

  const name = watch('name');
  const stateInput = watch('states');

  const handleOnSuccess = useCallback(
    (data: TSearchData, fname: string, lname: string, states: string[]) => {
      const url = getRedirectURL(data, { fname, lname, states });
      if (url) {
        navigate(url);
      } else {
        setError('name', { message: noResultsErrorMessage });
      }
    },
    [navigate, setError]
  );

  const handleOnError = useCallback(() => {
    setError('name', { message: noResultsErrorMessage });
  }, [setError]);

  const handleClearUnclaimedName = useCallback(() => {
    setValue('name', '');
    clearErrors('name');
  }, [setValue, clearErrors]);

  const handleStateChange = useCallback(
    (_event: unknown, value: OptionType[]) => {
      if (value.length > 5) return;
      setValue('states', value);
      if (value.length > 0) {
        clearErrors('states');
      }
    },
    [setValue, clearErrors]
  );

  const redirectToUnclaimedMoney = useCallback(
    (nameInput: IUnclaimedSearcParams) => {
      const params = getRedirectData(nameInput, stateInput);
      const fname = Array.isArray(params?.fname)
        ? params?.fname[0]
        : params?.fname;
      const lname = Array.isArray(params?.lname)
        ? params?.lname[0]
        : params?.lname;
      const states = params?.states;

      if (!fname || !lname || !states?.length) {
        return;
      }

      clearErrors('name');
      clearErrors('states');

      const options = {
        onSuccess: (data: TSearchData) =>
          handleOnSuccess(data, fname, lname, states),
        onError: handleOnError,
      };

      if (stateInput.length > 1) {
        searchUnclaimedMoneyMultiState.mutate(
          {
            first_name: fname,
            last_name: lname,
            states: params?.states,
          },
          options
        );
      } else if (stateInput.length === 1) {
        searchUnclaimedMoney.mutate(
          {
            first_name: fname,
            last_name: lname,
            state: states[0] ?? '',
          },
          options
        );
      }
    },
    [
      stateInput,
      clearErrors,
      searchUnclaimedMoneyMultiState,
      searchUnclaimedMoney,
      handleOnSuccess,
      handleOnError,
    ]
  );

  const onUnclaimedMoneySearchSubmit = useCallback(
    ({ name, states }: { name: string; states: OptionType[] }) => {
      if (!searchFormsRecyclingValidation(account)) {
        return;
      }

      const parsedName = parseFullName(name);

      if (!parsedName?.firstName || !parsedName?.lastName) {
        setError('name', { message: nameErrorMessage });
        return;
      }

      if (!states?.length) {
        setError('states', { message: statesErrorMessage });
        return;
      }

      trackEventGA4({
        event_name: 'report_search',
        report_type: 'unclaimed_money',
        search_from: `${placement}_search`,
      });

      redirectToUnclaimedMoney({
        fn: parsedName.firstName,
        ln: parsedName.lastName,
      });
    },
    [account, placement, redirectToUnclaimedMoney, setError, trackEventGA4]
  );

  const renderMobileForm = useCallback(() => {
    return (
      <Grid container sx={{ mt: 2.5, mb: 2.5 }} gap={3}>
        <UnclaimedMoneySearchContainer className={errors.name ? 'error' : ''}>
          <UnclaimedMoneyInput
            register={register}
            placeholder={placeholderText}
            errors={errors}
            clearErrors={(field?: string) => clearErrors(field as 'name')}
            onClear={handleClearUnclaimedName}
            value={name}
            control={control}
          />
        </UnclaimedMoneySearchContainer>
        <UnclaimedMoneySearchContainer className={errors.states ? 'error' : ''}>
          <UnclaimedMoneyStatesSelect
            id="mobile-states-select"
            error={errors.states}
            states={states}
            stateInput={stateInput}
            handleStateChange={handleStateChange}
            disableClearable
          />
        </UnclaimedMoneySearchContainer>
        <SearchButton type="submit" fullWidth>
          Search
        </SearchButton>
      </Grid>
    );
  }, [
    register,
    placeholderText,
    handleStateChange,
    name,
    errors,
    clearErrors,
    handleClearUnclaimedName,
    control,
    states,
    stateInput,
  ]);

  const renderDesktopForm = useCallback(() => {
    return (
      <UnclaimedMoneyFormContainer>
        <Box flexGrow={1}>
          <UnclaimedMoneySearchContainer
            container
            className={errors.name || errors.states ? 'error' : ''}
          >
            <Grid
              size={{ xs: 7, lg: 7.5 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <UnclaimedMoneyInput
                register={register}
                placeholder={placeholderText}
                errors={errors}
                clearErrors={(field?: string) => clearErrors(field as 'name')}
                onClear={handleClearUnclaimedName}
                value={name}
                control={control}
              />
            </Grid>
            <UnclaimedMoneySearchDivider />
            <Grid
              size={{ xs: 5, lg: 1 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <UnclaimedMoneyStatesSelect
                id="desktop-states-select"
                error={errors.states}
                states={states}
                stateInput={stateInput}
                handleStateChange={handleStateChange}
                disableClearable
              />
            </Grid>
          </UnclaimedMoneySearchContainer>
        </Box>
        <SearchButton type="submit">Search</SearchButton>
      </UnclaimedMoneyFormContainer>
    );
  }, [
    register,
    placeholderText,
    handleStateChange,
    name,
    errors,
    clearErrors,
    handleClearUnclaimedName,
    control,
    states,
    stateInput,
  ]);

  return (
    <form
      onSubmit={handleSubmit(onUnclaimedMoneySearchSubmit)}
      autoComplete="off"
    >
      {isMobile ? renderMobileForm() : renderDesktopForm()}
    </form>
  );
};
